import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  CalendarDaysIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  XCircleIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  PauseIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import publishingApi, { PublishingPlatform, ScheduledPost } from '@/services/publishingApi';
import RecurringScheduleModal from '@/components/Publishing/RecurringScheduleModal';

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PLATFORM_LABELS: Record<PublishingPlatform, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  whatsapp: 'WhatsApp',
  tiktok: 'TikTok',
  x: 'X',
};

const PLATFORM_COLORS: Record<PublishingPlatform, string> = {
  youtube: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  instagram: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  facebook: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  whatsapp: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  tiktok: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  x: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

function groupByDate(posts: ScheduledPost[]): Record<string, ScheduledPost[]> {
  return posts.reduce((acc, post) => {
    const day = new Date(post.scheduledFor).toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    if (!acc[day]) acc[day] = [];
    acc[day].push(post);
    return acc;
  }, {} as Record<string, ScheduledPost[]>);
}

export default function ScheduledPostsPage() {
  const queryClient = useQueryClient();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showRecurringModal, setShowRecurringModal] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['scheduled-posts'],
    queryFn: () => publishingApi.getScheduled(),
  });

  const { data: recurringData, isFetching: recurringFetching } = useQuery({
    queryKey: ['recurring-schedules'],
    queryFn: () => publishingApi.getRecurring(),
  });
  const recurringSchedules = recurringData?.schedules || [];

  const toggleRecurringMutation = useMutation({
    mutationFn: ({ scheduleId, isActive }: { scheduleId: string; isActive: boolean }) => publishingApi.toggleRecurring(scheduleId, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] }),
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update recurring schedule'),
  });

  const deleteRecurringMutation = useMutation({
    mutationFn: (scheduleId: string) => publishingApi.deleteRecurring(scheduleId),
    onSuccess: () => {
      toast.success('Recurring schedule deleted');
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to delete recurring schedule'),
  });

  const posts = data?.posts || [];
  const grouped = useMemo(() => groupByDate(posts), [posts]);

  const rescheduleMutation = useMutation({
    mutationFn: ({ platform, jobId, scheduledFor }: { platform: PublishingPlatform; jobId: string; scheduledFor: string }) =>
      publishingApi.reschedule(platform, jobId, scheduledFor),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Rescheduled');
        setEditingKey(null);
        queryClient.invalidateQueries({ queryKey: ['scheduled-posts'] });
      } else {
        toast.error(res.message || 'Failed to reschedule');
      }
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to reschedule'),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ platform, jobId }: { platform: PublishingPlatform; jobId: string }) => publishingApi.cancel(platform, jobId),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Cancelled');
        queryClient.invalidateQueries({ queryKey: ['scheduled-posts'] });
      } else {
        toast.error(res.message || 'Failed to cancel');
      }
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to cancel'),
  });

  const startEditing = (post: ScheduledPost) => {
    setEditingKey(`${post.platform}:${post.jobId}`);
    // datetime-local expects "YYYY-MM-DDTHH:mm"
    setEditValue(new Date(post.scheduledFor).toISOString().slice(0, 16));
  };

  const saveReschedule = (post: ScheduledPost) => {
    if (!editValue) return;
    rescheduleMutation.mutate({
      platform: post.platform,
      jobId: post.jobId,
      scheduledFor: new Date(editValue).toISOString(),
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CalendarDaysIcon className="h-7 w-7 text-indigo-600" />
            Scheduled Posts
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Every upcoming YouTube and social publish, in one place — reschedule or cancel before it goes live.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recurring schedules</h2>
          <button
            type="button"
            onClick={() => setShowRecurringModal(true)}
            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            <PlusIcon className="h-4 w-4" />
            New recurring schedule
          </button>
        </div>
        {recurringFetching && recurringSchedules.length === 0 ? (
          <div className="h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        ) : recurringSchedules.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No recurring schedules set up.</p>
        ) : (
          <div className="space-y-2">
            {recurringSchedules.map((schedule) => (
              <div
                key={schedule.scheduleId}
                className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${PLATFORM_COLORS[schedule.platform]}`}>
                      {PLATFORM_LABELS[schedule.platform]}
                    </span>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {schedule.frequency === 'daily'
                        ? 'Daily'
                        : `Weekly · ${(schedule.daysOfWeek || []).map((d) => DAY_ABBR[d]).join(', ')}`}
                    </p>
                    {!schedule.isActive && <span className="text-xs text-amber-600 dark:text-amber-400">Paused</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    Next: {new Date(schedule.nextRunAt).toLocaleString()}
                    {schedule.lastSkipReason ? ` · ${schedule.lastSkipReason}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleRecurringMutation.mutate({ scheduleId: schedule.scheduleId, isActive: !schedule.isActive })}
                    disabled={toggleRecurringMutation.isLoading}
                    title={schedule.isActive ? 'Pause' : 'Resume'}
                    className="p-1.5 rounded-full text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-50"
                  >
                    {schedule.isActive ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteRecurringMutation.mutate(schedule.scheduleId)}
                    disabled={deleteRecurringMutation.isLoading}
                    title="Delete"
                    className="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Upcoming one-off posts</h2>
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center">
          <CalendarDaysIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Nothing scheduled right now.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([day, dayPosts]) => (
            <div key={day}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{day}</h2>
              <div className="space-y-2">
                {dayPosts.map((post) => {
                  const key = `${post.platform}:${post.jobId}`;
                  const isEditing = editingKey === key;
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${PLATFORM_COLORS[post.platform]}`}>
                          {PLATFORM_LABELS[post.platform]}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{post.title}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(post.scheduledFor).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                            {' · '}
                            {post.status}
                          </p>
                        </div>
                      </div>

                      {isEditing ? (
                        <div className="flex items-center gap-2 shrink-0">
                          <input
                            type="datetime-local"
                            value={editValue}
                            min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={() => saveReschedule(post)}
                            disabled={rescheduleMutation.isLoading}
                            title="Save"
                            className="p-1.5 rounded-full text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingKey(null)}
                            title="Cancel edit"
                            className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => startEditing(post)}
                            title="Reschedule"
                            className="p-1.5 rounded-full text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => cancelMutation.mutate({ platform: post.platform, jobId: post.jobId })}
                            disabled={cancelMutation.isLoading}
                            title="Cancel"
                            className="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                          >
                            <XCircleIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showRecurringModal && (
        <RecurringScheduleModal
          onClose={() => setShowRecurringModal(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] })}
        />
      )}
    </div>
  );
}
